import { Modal } from "@general-backoffice/core";
import { Header, Loading, notify } from "@general-backoffice/core/index";
import {
  BOT_CONFIG_POPULATE_FULL,
  type BotConfig,
  type Event,
  type User,
  UserConfigSchema,
} from "@investhinks/api-client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { Badge, Button, Card as RTCard, CardBody as RTCardBody, Col, Container, Row } from "reactstrap";

import { botConfigsEndpoints, eventsEndpoints, exchangeAccountsEndpoints } from "../../../../api";
import { useAccountMovementsModal } from "../../../../components/bots/AccountMovementsModal";
import BotEventsCard from "../../../../components/bots/BotEventsCard";
import BotStatusToggleModal from "../../../../components/bots/BotStatusToggleModal";
import CancelSubscriptionModal from "../../../../components/bots/CancelSubscriptionModal";
import useBot from "../../../../components/bots/hooks/useBot";
import useBots from "../../../../components/bots/hooks/useBots";
import SettingsDrawer from "../../../../components/bots/SettingsDrawer";
import TradesHistoryCard from "../../../../components/bots/TradesHistoryCard";
import UpdateBotModal from "../../../../components/bots/UpdateBotModal";
import UpdateBotPaymentMethodModal from "../../../../components/bots/UpdateBotPaymentMethodModal";
import ChargesTableCard from "../../../../components/charges/ChargesTableCard";
import useCharges from "../../../../components/charges/hooks/useCharges";
import useTrustpilotInvitation from "../../../../components/charges/hooks/useTrustpilotInvitation";
import RetryPaymentButton from "../../../../components/charges/RetryPaymentButton";
import useExchangeAccount from "../../../../components/exchangeAccounts/hooks/useExchangeAccount";
import ConnectorPanel from "../../../../components/exchangeAccounts/ConnectorPanel";
import MoveExchangeAccountModal from "../../../../components/exchangeAccounts/MoveExchangeAccountModal";
import UpdateExchangeAccountModal from "../../../../components/exchangeAccounts/UpdateExchangeAccountModal";
import useRefunds from "../../../../components/refunds/hooks/useRefunds";
import RefundsTableCard from "../../../../components/refunds/RefundsTableCard";
import CardButton from "../../../../components/shared/CardButton";
import BotPerformanceGraphCard from "../../../../components/stats/BotPerformanceGraphCard";
import UserCard from "../../../../components/users/UserCard";
import type { RootState } from "../../../../redux/reducers/session/state";
import getCurrencySymbol from "../../../../utils/getCurrencySymbol";
import getErrorMessage from "../../../../utils/getErrorMessage";
import getStoploss from "../../../../utils/getStoploss";
import isVipOrZeroPriceTier from "../../../../utils/isVipOrZeroPriceTier";
import { ActivitySidebar, ConfigSidebar, PerformanceSidebar,PeriodStatisticsCard } from "./components";

// Component props
interface ExchangeAccountProps {
  hidePrivateInfo?: boolean;
}

// UI state type for bot states
interface BotState {
  key: string;
  name: string | React.ReactNode;
  badgeName?: string;
  color: string;
  actionColor?: string;
  actionText: string;
  allowActionInList: boolean;
  allowAutoAction: boolean;
  action?: () => void;
}

const pendingStates = ["PENDING", "VALIDATING"];

const ExchangeAccount: React.FC<ExchangeAccountProps> = ({ hidePrivateInfo = false }) => {
  const { t } = useTranslation("dashboard", { keyPrefix: "exchangeAccount" });
  const { t: tArchive } = useTranslation("dashboard", { keyPrefix: "exchangeAccount.archiveConfirm" });
  const sessionUser = useSelector((state: RootState) => state.sessionState?.user);
  const isAdmin = sessionUser?.role?.name === "Admin";
  const { exchangeAccountId, userId, referralId } = useParams();
  // Viewing own account if no userId/referralId in URL (client routes)
  const isViewingOwnAccount = !userId && !referralId;
  const navigate = useNavigate();
  const refAutoActionsActive = useRef<boolean>(isViewingOwnAccount);
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const {
    exchangeAccount,
    load: loadExchangeAccount,
    isLoading: isLoadingExchangeAccount,
  } = useExchangeAccount(exchangeAccountId);

  const id = exchangeAccount?.id || exchangeAccountId;

  const {
    exchangeAccount: exchangeAccountToUpdate,
    setExchangeAccount: setExchangeAccountToUpdate,
    update: updateExchangeAccount,
    isLoading: isUpdatingExchangeAccount,
  } = useExchangeAccount();

  const {
    bots: [bot] = [],
    isLoading: isLoadingBots,
    refresh: refreshBots,
  } = useBots({
    autoLoad: !!id,
    filters: { exchange_account: { id: id } },
    // "history" and "statistics.history" are the same series; request it once
    populate: [...BOT_CONFIG_POPULATE_FULL.filter((p) => p !== "statistics.history"), "history"],
    sort: { createdAt: "down" },
    pagination: { pageSize: 1 },
    depsToReset: [id],
    mine: isViewingOwnAccount,
    cacheKey: id ? `bot_details_ea_${id}` : undefined,
  });

  // Track if initial bot load has completed for this exchange account
  // This prevents auto-actions from firing before we know if a bot exists
  const [hasBotsLoaded, setHasBotsLoaded] = useState(false);
  useEffect(() => {
    // Reset when exchange account changes
    setHasBotsLoaded(false);
  }, [id]);
  useEffect(() => {
    // Mark as loaded once loading completes (and we have an id to load for)
    // If there's no id, there's no exchange account so no bots to load
    if (!id || !isLoadingBots) {
      setHasBotsLoaded(true);
    }
  }, [isLoadingBots, id]);

  // Partial bot with just exchange_account - used to pre-fill the create bot modal
  // Uses id: 0 as sentinel for "new bot" (modal checks bot?.id to determine create vs update)
  const defaultBot = useMemo<BotConfig>(
    () => ({
      id: 0,
      disabled: false,
      exchange_account: exchangeAccount,
    }),
    [exchangeAccount]
  );

  const { bot: botToUpdate, setBot: setBotToUpdate, update: updateBot, isLoading: isUpdatingBot } = useBot();

  const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] = useState(false);
  const [showSettingsDrawer, setShowSettingsDrawer] = useState<boolean>(false);
  const [showMoveModal, setShowMoveModal] = useState<boolean>(false);
  const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] = useState<boolean>(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState<boolean>(false);
  const [isArchiving, setIsArchiving] = useState<boolean>(false);

  const handleToggleArchive = async () => {
    if (!ea?.id || isArchiving) return;
    setIsArchiving(true);
    try {
      await exchangeAccountsEndpoints.updateExchangeAccount(ea.id, { hidden: !hidden });
      // Close before reloading: `hidden` selects which dialog renders, so
      // leaving it open while the value flips flashed the opposite dialog.
      setShowArchiveConfirm(false);
      await loadExchangeAccount();
    } catch (err: unknown) {
      setShowArchiveConfirm(false);
      notify.error(getErrorMessage(err, tArchive("error")));
    } finally {
      setIsArchiving(false);
    }
  };

  const { bot: botToToggle, setBot: setBotToToggle, update: updateBotToToggle, isLoading: isTogglingBot } = useBot();

  const { Modal: AccountMovementsModal, open: openAccountMovementsModal } = useAccountMovementsModal();

  const { charges, ...chargesProps } = useCharges({
    mine: isViewingOwnAccount,
    autoLoad: !!bot?.id && !hidePrivateInfo,
    depsToReset: [bot?.id],
    filters: { bot_config: { id: bot?.id } },
    pagination: { pageSize: 5 },
    sort: { createdAt: "down" },
    cacheKey: bot?.id ? `charges_bot_${bot.id}` : undefined,
  });

  const { refunds, ...refundsProps } = useRefunds({
    mine: isViewingOwnAccount,
    autoLoad: !!bot?.id && !hidePrivateInfo,
    depsToReset: [bot?.id],
    filters: { bot_config: { id: bot?.id } },
    pagination: { pageSize: 5 },
    sort: { createdAt: "down" },
    cacheKey: bot?.id ? `refunds_bot_${bot.id}` : undefined,
  });

  useTrustpilotInvitation(isViewingOwnAccount && !!charges?.length);

  const [botEvents, setBotEvents] = useState<Event[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState<boolean>(false);

  useEffect(() => {
    if (!bot?.uuid || hidePrivateInfo) {
      setBotEvents([]);
      return;
    }

    setIsLoadingEvents(true);
    eventsEndpoints
      .getMyEvents({ configUid: bot.uuid, limit: 50 })
      .then(({ data }) => {
        setBotEvents(data || []);
      })
      .catch((err: unknown) => {
        console.error("Error loading bot events:", err);
        setBotEvents([]);
      })
      .finally(() => {
        setIsLoadingEvents(false);
      });
  }, [bot?.uuid, hidePrivateInfo]);

  // Extract status for dependency - only re-run when status changes
  const exchangeAccountStatus = exchangeAccount?.status;

  useEffect(() => {
    clearInterval(intervalRef.current);
    if (exchangeAccountStatus && pendingStates.includes(exchangeAccountStatus)) {
      intervalRef.current = setInterval(() => {
        loadExchangeAccount(id).then((nea) => {
          switch (nea?.status) {
            case "VALID":
              notify.success(t("validationSuccessText"));
              // Only set bot if there's no existing bot - use partial bot to pre-fill create modal
              if (!bot && defaultBot) {
                setBotToUpdate(defaultBot);
              }
              break;
            case "INVALID":
              notify.error(t(`validationErrorsText.${nea?.error || "NONE"}`));
              break;
          }
        });
      }, 5000);
    }
    return () => clearInterval(intervalRef.current);
  }, [id, exchangeAccountStatus, loadExchangeAccount, t, bot, defaultBot, setBotToUpdate]);

  // Destructure exchangeAccount fields
  const ea = exchangeAccount;
  const { status, name, hidden, error } = ea || {};

  // Destructure bot fields
  // BotConfig includes history and accountMovements as nullish fields from the schema
  const {
    userConfig: rawUserConfig,
    strategy,
    stats,
    disabled,
    subscription,
    user: botUser,
    exchange_account: botExchangeAccount,
    accountMovements,
    history,
  } = bot || {};

  // Parse userConfig from generic Record to typed UserConfig
  const userConfig = useMemo(() => {
    if (!rawUserConfig) return undefined;
    const result = UserConfigSchema.safeParse(rawUserConfig);
    return result.success ? result.data : undefined;
  }, [rawUserConfig]);

  // Get user from bot.user, or from exchange_account.user if populated, or sessionUser only when viewing own account
  // Note: z.lazy() causes bot.user and exchange_account.user to be inferred as {} by TypeScript,
  // but at runtime they are properly-shaped UserRelation objects when populated.
  const exchangeAccountUser =
    botExchangeAccount && typeof botExchangeAccount === "object" && "user" in botExchangeAccount
      ? botExchangeAccount.user
      : undefined;
  const rawUser = botUser || exchangeAccountUser || (isViewingOwnAccount ? sessionUser : undefined);
  const user: User | null | undefined =
    rawUser && typeof rawUser === "object" && "email" in rawUser ? (rawUser as User) : undefined;

  const totalMovements = (accountMovements || []).reduce(
    (acc: { deposit: number; withdrawal: number }, movement) => {
      if (movement.type === "DEAL_TYPE_BALANCE") {
        if (movement.amount > 0) acc.deposit = acc.deposit + movement.amount;
        if (movement.amount < 0) acc.withdrawal = acc.withdrawal - movement.amount;
      }
      return acc;
    },
    { deposit: 0, withdrawal: 0 }
  );

  const isPending = status ? pendingStates.includes(status) : false;
  const isInvalid = status === "INVALID";

  const hasPaymentError =
    !isVipOrZeroPriceTier(user) && ["failed", "unpaid", "failed_escalated"].includes(subscription?.pm_status ?? "");
  const hasCredentialsError = error === "INVALID_CREDENTIALS";
  const hasTooLowLeverage = error === "LEVERAGE_TOO_LOW";
  const hasInvalidAccountType = error === "INVALID_ACCOUNT_TYPE";

  const handleUnarchive = (): void => {
    if (!ea?.id) return;
    exchangeAccountsEndpoints.updateExchangeAccount(ea.id, { hidden: false }).then(() => loadExchangeAccount(id));
  };

  const states: Record<string, BotState> = {
    active: {
      key: "active",
      name: t("states.active.name"),
      color: "success",
      actionText: t("states.active.actionText"),
      allowActionInList: false,
      allowAutoAction: false,
      action: () => setBotToToggle(bot),
    },
    paused: {
      key: "paused",
      name: t("states.paused.name"),
      color: "warning",
      actionText: t("states.paused.actionText"),
      allowActionInList: true,
      allowAutoAction: false,
      action: () => setBotToToggle(bot),
    },
    archived: {
      key: "archived",
      name: t("states.archived.name"),
      badgeName: t("states.archived.badge"),
      color: "secondary",
      actionColor: "primary",
      actionText: t("states.archived.actionText"),
      allowActionInList: true,
      allowAutoAction: false,
      action: handleUnarchive,
    },
    notCreated: {
      key: "notCreated",
      name: t("states.notCreated.name"),
      color: "danger",
      actionColor: "danger",
      actionText: t("states.notCreated.actionText"),
      allowActionInList: true,
      allowAutoAction: true,
      action: () => setExchangeAccountToUpdate({ id: 0 }),
    },
    notConnected: {
      key: "notConnected",
      name: t("states.notConnected.name"),
      color: "danger",
      actionColor: "danger",
      actionText: t("states.notConnected.actionText"),
      allowActionInList: true,
      allowAutoAction: true,
      action: () => setBotToUpdate(defaultBot),
    },
    needsPayment: {
      key: "needsPayment",
      name: t("states.needsPayment.name"),
      color: "danger",
      actionColor: "danger",
      actionText: t("states.needsPayment.actionText"),
      allowActionInList: true,
      allowAutoAction: false,
      action: () => setIsPaymentMethodModalOpen(true),
    },
    notVerified: {
      key: "notVerified",
      name: (
        <>
          {t("states.notVerified.name")}:<br />
          <b>{t(`validationErrorsText.${ea?.error || "NONE"}`)}</b>
          <br />
          {t("states.notVerified.text")}
        </>
      ),
      color: "danger",
      actionColor: "danger",
      actionText: t("states.notVerified.actionText"),
      allowActionInList: true,
      allowAutoAction: false,
      action: () => setExchangeAccountToUpdate(ea ?? null),
    },
    tooLowLeverage: {
      key: "tooLowLeverage",
      name: (
        <div>
          {t("states.tooLowLeverage.name")} {t("states.tooLowLeverage.text")}
        </div>
      ),
      color: "danger",
      actionColor: "danger",
      actionText: t("states.tooLowLeverage.actionText"),
      allowActionInList: true,
      allowAutoAction: false,
      action: () => setExchangeAccountToUpdate(ea ?? null),
    },
    invalidAccountType: {
      key: "invalidAccountType",
      name: (
        <div>
          {t("states.invalidAccountType.name")} {t("states.invalidAccountType.text")}
        </div>
      ),
      color: "danger",
      actionColor: "danger",
      actionText: t("states.invalidAccountType.actionText"),
      allowActionInList: true,
      allowAutoAction: false,
      action: () => setExchangeAccountToUpdate(ea ?? null),
    },
    verifying: {
      key: "verifying",
      name: t("states.verifying.name"),
      color: "info",
      actionText: t("states.verifying.actionText"),
      allowActionInList: false,
      allowAutoAction: false,
      action: undefined,
    },
    loading: {
      key: "loading",
      name: t("states.loading.name"),
      color: "info",
      actionText: t("states.loading.actionText"),
      allowActionInList: false,
      allowAutoAction: false,
      action: undefined,
    },
  };

  const areLoading = isLoadingBots || isLoadingExchangeAccount || isPending || !hasBotsLoaded;

  let state: BotState;
  if (isLoadingExchangeAccount) state = states.loading;
  else if (isLoadingBots) state = states.loading;
  else if (!hasBotsLoaded)
    state = states.loading; // Wait for initial bot load before determining state
  else if (isPending) state = states.verifying;
  else if (!exchangeAccount) state = states.notCreated;
  else if (isInvalid) {
    if (hasCredentialsError) state = states.notVerified;
    else if (hasTooLowLeverage) state = states.tooLowLeverage;
    else if (hasInvalidAccountType) state = states.invalidAccountType;
    else state = states.notVerified;
  } else if (hidden) state = states.archived;
  else if (!bot) state = states.notConnected;
  else if (hasPaymentError) state = states.needsPayment;
  else if (disabled) state = states.paused;
  else state = states.active;

  // Track if auto-action has been executed for this state
  const autoActionExecutedRef = useRef<string | null>(null);

  useEffect(() => {
    // Only execute auto-action once per state key
    if (
      refAutoActionsActive.current &&
      state.allowAutoAction &&
      state.action &&
      autoActionExecutedRef.current !== state.key
    ) {
      autoActionExecutedRef.current = state.key;
      state.action();
    }
  }, [state.key, state.allowAutoAction, state.action]);

  const stopLoss = getStoploss(
    strategy ? { name: strategy.name, configOptions: strategy.configOptions } : undefined,
    userConfig
  );
  const symbol = useMemo(() => getCurrencySymbol(stats?.baseCurrency), [stats?.baseCurrency]);

  return (
    <React.Fragment>
      {exchangeAccountToUpdate && (
        <UpdateExchangeAccountModal
          exchangeAccount={exchangeAccountToUpdate}
          setExchangeAccount={setExchangeAccountToUpdate}
          close={() => setExchangeAccountToUpdate(null)}
          update={() => {
            return updateExchangeAccount()
              .then(async (result) => {
                const newId = result?.id;
                if (!exchangeAccountId && newId) navigate(`/exchange-account/${newId}`);
                if (newId) await loadExchangeAccount(newId);
                setExchangeAccountToUpdate(null);
              })
              .catch(() => {});
          }}
          isLoading={isUpdatingExchangeAccount}
        />
      )}

      <UpdateBotModal
        bot={botToUpdate}
        originalBot={bot}
        setBot={setBotToUpdate}
        exchangeType={ea?.exchange?.type ?? undefined}
        close={() => setBotToUpdate(null)}
        update={(options) => {
          return updateBot(undefined, options)
            .then(async () => {
              await refreshBots();
              setBotToUpdate(null);
            })
            .catch(() => {});
        }}
        isLoading={isUpdatingBot}
      />

      <UpdateBotPaymentMethodModal
        isOpen={isPaymentMethodModalOpen}
        close={() => setIsPaymentMethodModalOpen(false)}
        update={async (paymentMethodId) => {
          try {
            const botId = bot?.id;
            if (botId) {
              await botConfigsEndpoints.changePaymentMethod(botId, paymentMethodId);
            }
            await refreshBots();
            setIsPaymentMethodModalOpen(false);
          } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : "Error updating payment method";
            notify.error(errorMessage);
          }
        }}
        isLoading={isUpdatingBot}
      />

      <BotStatusToggleModal
        bot={botToToggle}
        close={() => setBotToToggle(null)}
        toggle={() => {
          if (!botToToggle) return Promise.resolve();
          return updateBotToToggle({ ...botToToggle, disabled: !botToToggle.disabled })
            .then(async () => {
              await refreshBots();
              setBotToToggle(null);
            })
            .catch(() => null);
        }}
        isLoading={isTogglingBot}
      />

      {AccountMovementsModal && (
        <AccountMovementsModal
          accountMovements={accountMovements ?? undefined}
          currency={stats?.baseCurrency ?? undefined}
        />
      )}

      <SettingsDrawer
        isOpen={showSettingsDrawer}
        onClose={() => setShowSettingsDrawer(false)}
        exchangeAccount={ea}
        bot={bot}
        user={user}
        onEditCredentials={() => {
          setShowSettingsDrawer(false);
          setExchangeAccountToUpdate(ea ?? { id: 0 });
        }}
        onEditBot={() => {
          setShowSettingsDrawer(false);
          setBotToUpdate(bot || defaultBot);
        }}
        onChangePayment={() => {
          setShowSettingsDrawer(false);
          setIsPaymentMethodModalOpen(true);
        }}
        onToggleBot={() => {
          setShowSettingsDrawer(false);
          setBotToToggle(bot ?? null);
        }}
        onHideAccount={() => {
          if (!ea?.id) return;
          setShowSettingsDrawer(false);
          setShowArchiveConfirm(true);
        }}
        isAdmin={isAdmin}
        canEdit={isViewingOwnAccount}
        onMoveAccount={() => {
          setShowSettingsDrawer(false);
          setShowMoveModal(true);
        }}
        onCancelSubscription={() => {
          setShowSettingsDrawer(false);
          setShowCancelSubscriptionModal(true);
        }}
      />

      {/* Archiving sits in the danger zone but used to fire on a single click,
          with no confirmation and no error handling. Restoring is not
          destructive, so it uses the neutral confirm rather than the red
          delete dialog. */}
      {hidden ? (
        <Modal.SaveConfirm
          isOpen={showArchiveConfirm}
          isLoading={isArchiving}
          toggleOpen={() => setShowArchiveConfirm(false)}
          titleSave={tArchive("showTitle")}
          descriptionSave={tArchive("showDescription", { name: ea?.name ?? "" })}
          closeText={tArchive("closeBtn")}
          buttonSave={{
            children: tArchive("showSubmit"),
            disabled: isArchiving,
            onClick: handleToggleArchive,
          }}
        />
      ) : (
        <Modal.DeleteConfirm
          isOpen={showArchiveConfirm}
          isLoading={isArchiving}
          toggleOpen={() => setShowArchiveConfirm(false)}
          titleDelete={tArchive("hideTitle")}
          descriptionDelete={tArchive("hideDescription", { name: ea?.name ?? "" })}
          closeText={tArchive("closeBtn")}
          buttonDelete={{
            children: tArchive("hideSubmit"),
            disabled: isArchiving,
            onClick: handleToggleArchive,
          }}
        />
      )}



      <MoveExchangeAccountModal
        isOpen={showMoveModal}
        onClose={() => setShowMoveModal(false)}
        exchangeAccount={ea}
        currentUser={user}
        bot={bot}
        onSuccess={() => {
          loadExchangeAccount();
          refreshBots();
        }}
      />

      <CancelSubscriptionModal
        isOpen={showCancelSubscriptionModal}
        onClose={() => setShowCancelSubscriptionModal(false)}
        subscription={subscription}
        bot={bot}
        onSuccess={() => {
          loadExchangeAccount();
          refreshBots();
        }}
      />

      {areLoading && <Loading.Line />}
      <Header.BreadCrumb
        color="secondary"
        isDark={false}
        title={
          <div className="d-flex align-items-center">
            {!!hidden && <i className="fas fa-eye-slash mr-2" />}
            <span>{name || t("states.loading.name")}</span>
            <Badge color={state.color} className="ml-3 px-3 py-2 badge-status-dot">
              {state.badgeName ||
                (typeof state.name === "string"
                  ? state.name
                  : state.key === "active"
                    ? t("states.active.name")
                    : state.key === "paused"
                      ? t("states.paused.name")
                      : "")}
            </Badge>
            <Button
              color="link"
              className="text-dark ml-3 p-0"
              onClick={() => setShowSettingsDrawer(true)}
              disabled={areLoading}
            >
              <i className="fas fa-cog fa-lg" />
            </Button>
          </div>
        }
      />
      <Container fluid className="mt--6 mb-3 position-relative">
        <Row>
          {/* Sidebar */}
          <Col xs={12} lg={3}>
            <div className="sticky-top" style={{ top: "1rem" }}>
              {/* User Card (when viewing others' accounts) */}
              {!isViewingOwnAccount && user && (
                <div className="mb-3">
                  <UserCard
                    user={user}
                    title={user.name || user.email || "User"}
                    onClick={() => navigate(`/users/${user.id}`)}
                  />
                </div>
              )}

              {/* Action Alert - Show if action needed (only when viewing own account) */}
              {state.allowActionInList && isViewingOwnAccount && (
                <RTCard className="mb-3 border-left-4" style={{ borderLeftColor: `var(--${state.color})` }}>
                  <RTCardBody className="py-3">
                    <div className="small text-muted mb-2">{t("stateLabel")}</div>
                    <div className="mb-3">{state.name}</div>
                    <CardButton
                      className="text-nowrap w-100"
                      color={state.actionColor || state.color}
                      disabled={areLoading}
                      onClick={state.action}
                      center={true}
                    >
                      {state.actionText}
                    </CardButton>
                    {state.key === "needsPayment" && subscription?.id && (
                      <div className="mt-2">
                        <RetryPaymentButton subscriptionId={subscription.id} block onRetried={() => refreshBots()} />
                      </div>
                    )}
                  </RTCardBody>
                </RTCard>
              )}

              {/* Performance Stats */}
              <PerformanceSidebar stats={stats ?? undefined} symbol={symbol} t={t} />

              {/* Activity Stats */}
              <ActivitySidebar
                stats={stats ?? undefined}
                symbol={symbol}
                totalMovements={totalMovements}
                openAccountMovementsModal={openAccountMovementsModal}
                t={t}
              />

              {/* Configuration Summary */}
              {bot && (
                <ConfigSidebar
                  strategy={strategy}
                  userConfig={userConfig}
                  stopLoss={stopLoss ?? undefined}
                  symbol={symbol}
                  onEditBot={() => setBotToUpdate(bot)}
                  canEdit={isViewingOwnAccount}
                  t={t}
                />
              )}
            </div>
          </Col>

          {/* Main Content */}
          <Col xs={12} lg={9}>
            {/* Performance Chart - Full Width */}
            <BotPerformanceGraphCard
              data={history ?? undefined}
              currency={stats?.baseCurrency ?? undefined}
              stopLoss={stopLoss ?? undefined}
              sailingMode={
                strategy?.configOptions?.sailingMode ? (userConfig?.sailingModeThreshold ?? undefined) : undefined
              }
              isLoading={areLoading}
            />

            {/* Period Statistics - Admin Only */}
            {isAdmin && !!history?.length && (
              <PeriodStatisticsCard data={history} currency={stats?.baseCurrency ?? undefined} />
            )}

            {/* Native connector - Admin Only */}
            {isAdmin && !!ea?.id && <ConnectorPanel exchangeAccountId={ea.id} />}

            {/* Trades History - Full Width */}
            <TradesHistoryCard bot={bot} isLoading={areLoading} />

            {/* Charges + Refunds - Side by side if refunds exist, otherwise charges full width */}
            {!hidePrivateInfo && (
              <Row>
                <Col xs={12} lg={refunds?.length ? 6 : 12}>
                  <ChargesTableCard
                    {...chargesProps}
                    charges={charges}
                    compact={!!refunds?.length}
                    subscription={subscription}
                  />
                </Col>
                {!!refunds?.length && (
                  <Col xs={12} lg={6}>
                    <RefundsTableCard {...refundsProps} refunds={refunds} compact />
                  </Col>
                )}
              </Row>
            )}

            {/* Bot Events - Collapsed by default */}
            {!!botEvents?.length && (
              <BotEventsCard events={botEvents} isLoading={isLoadingEvents} defaultCollapsed={true} />
            )}
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default ExchangeAccount;
